import { useState } from "react";
import { useRouter } from "next/router";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import PasswordStrengthBar from "react-password-strength-bar";
import * as Yup from "yup";
import PasswordField from "@/components/CreateObject/formFields/PasswordField";
import { OrangeButton } from "@/lib/chakra/components";
import { useAppSelector } from "@/lib/hooks";
import { IBreakpoint } from "@/lib/interfaces";
import { usePasswordResetMutation } from "@/lib/services/appApi";
import {
  useLazyGetUserPRQuery,
  useUserPRMutation,
} from "@/lib/services/endpoints/newAPI/user";
import { GetBreakpoint, IsLargeOrBigger, isMobile } from "@/utils/common";
import { GetLogo } from "../ClientBased";
import AppAlert from "../utils/AppAlert";

interface Values {
  email: string;
}
export interface PasswordReset {
  password: string;
  confirmPassword: string;
}

export const PasswordReset = () => {
  const { hostname } = useAppSelector((state) => state.app);
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const bg = useColorModeValue("#ffffff", "#1a202c");
  const text = useColorModeValue("#232323", "#ffffff");
  const breakpoint: IBreakpoint = GetBreakpoint() as IBreakpoint;
  const [passwordReset] = usePasswordResetMutation();

  const form = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required."),
    }),
    onSubmit: async ({ email }: Values) => {
      setLoading(true);
      const resetUrl =
        window.location.protocol + "//" + hostname + "/resetPassword";
      // const res =
      await passwordReset({
        email,
        resetUrl,
      });
      setRequestSent(true);
      setLoading(false);
    },
  });

  return (
    <>
      <GetLogo />
      <Text
        my={3}
        color={text}
        fontSize={isMobile(breakpoint) ? "2xl" : "2xl"}
        fontWeight={"bold"}
      >
        Password reset
      </Text>

      <Flex direction="column" display={requestSent ? "none" : "flex"} w="full">
        <Text color="#8F8F8F">
          Please enter your email to search for your account.
        </Text>
        <form
          style={{ width: "100%" }}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit(e);
          }}
        >
          <FormControl
            my={6}
            id="email"
            isInvalid={
              form.errors.email !== "" && form.errors.email !== undefined
            }
          >
            <FormLabel fontWeight="bold" htmlFor="email" fontSize="sm">
              Email
            </FormLabel>
            <Input
              autoFocus={true}
              value={form.values.email}
              onChange={form.handleChange}
              name="email"
              disabled={loading}
              type="email"
              autoComplete="email"
              fontSize={"md"}
            />
            <FormErrorMessage>{form.errors.email}</FormErrorMessage>
          </FormControl>
          <Flex gap={5}>
            <Button
              onClick={() => {
                router.push("/login");
              }}
              variant={"outline"}
              colorScheme={"red"}
              isLoading={loading}
              w="full"
            >
              Cancel
            </Button>
            <OrangeButton
              isDisabled={!form.isValid}
              type="submit"
              isLoading={loading}
              w="full"
            >
              Submit
            </OrangeButton>
          </Flex>
        </form>
      </Flex>
      <Flex
        display={!requestSent ? "none" : "flex"}
        direction="column"
        justifyContent={"flex-start"}
        gap={5}
      >
        <AppAlert
          mt={5}
          status="success"
          title=""
          description="If the email address you provided is correct, we have sent you an email with a link to reset your password. Please check your inbox."
        />
        <Button
          onClick={() => {
            router.push("/login");
          }}
          isLoading={loading}
          w="full"
        >
          Go back
        </Button>
      </Flex>
    </>
  );
};

type ResetPasswordProps = {
  auth: string;
};

export const ResetPassword = ({ auth }: ResetPasswordProps) => {
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const [error, setError] = useState("");
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const bg = useColorModeValue("#ffffff", "#1a202c");
  const text = useColorModeValue("#232323", "#ffffff");
  const breakpoint: IBreakpoint = GetBreakpoint() as IBreakpoint;
  const [passwordReset] = usePasswordResetMutation();
  const [getUser] = useLazyGetUserPRQuery();
  const [updatePassword] = useUserPRMutation();
  const toast = useToast();

  const form = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required("Password is required")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!+@#\$%\^&\*])(?=.{8,})/,
          "Must Contain a minimum of 8 Characters, with at least one Uppercase, one Lowercase, one Number and one Special Case Character"
        ),

      confirmPassword: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref("password")], "Passwords do not match"),
    }),
    onSubmit: async ({ password }: PasswordReset) => {
      setLoading(true);
      setError("");
      try {
        const res = await passwordReset({ auth }).unwrap();
        if (res.success === true) {
          const token = res.token as string;
          const userId = res.data.uId;

          const user = await getUser({
            user: userId,
            token,
          }).unwrap();

          if (user.status === "success") {
            const update = await updatePassword({
              user: userId,
              token,
              method: "PUT",
              body: { ...user.data[0], password },
            }).unwrap();

            if (update.status === "success") {
              toast({
                description:
                  "You have successfully reset your password. Please login with your new password.",
                status: "success",
              });
              router.push("/login");
            }
          }
        } else {
          setLoading(false);
        }
      } catch (e) {
        let errorMessage = "An error occurred";
        if (e?.data?.data?.code === 410) {
          errorMessage =
            "Your password reset link has expired. Please trigger another one. ";
        }
        setError(errorMessage);
      }
      setLoading(false);
    },
  });

  return (
    <Flex bg={bg} w="100%" h="100vh" justifyContent={"space-between"}>
      <Flex
        w={IsLargeOrBigger(breakpoint) ? "50%" : "100%"}
        direction={"column"}
        alignItems="flex-start"
        pt={"15%"}
        px={isMobile(breakpoint) ? 10 : 20}
      >
        <GetLogo />
        <Text
          my={3}
          color={text}
          fontSize={isMobile(breakpoint) ? "2xl" : "5xl"}
          fontWeight={"bold"}
        >
          Password reset
        </Text>

        <Flex
          direction="column"
          display={requestSent ? "none" : "flex"}
          w="full"
        >
          <form
            style={{ width: "100%" }}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit(e);
            }}
          >
            <Flex direction={"column"} gap={5}>
              {error.length > 0 ? (
                <AppAlert status="error" description={error} />
              ) : (
                <></>
              )}

              <PasswordField
                accessor="password"
                label="Password"
                getFieldProps={form.getFieldProps}
                touched={form.touched}
                errors={form.errors}
                isSubmitting={form.isSubmitting}
              />

              {form.values.password.length > 0 ? (
                <PasswordStrengthBar
                  minLength={8}
                  password={form.values.password}
                />
              ) : (
                <></>
              )}

              <PasswordField
                accessor="confirmPassword"
                label="Password Confirm"
                getFieldProps={form.getFieldProps}
                touched={form.touched}
                errors={form.errors}
                isSubmitting={form.isSubmitting}
              />
            </Flex>

            <Flex gap={5} mt={5}>
              <Button
                onClick={() => {
                  router.push("/login");
                }}
                variant={"outline"}
                colorScheme={"red"}
                isLoading={loading}
                w="full"
              >
                Back to login
              </Button>
              <OrangeButton
                isDisabled={!form.isValid}
                type="submit"
                isLoading={loading}
                w="full"
              >
                Submit
              </OrangeButton>
            </Flex>
          </form>
        </Flex>
      </Flex>

      <Flex
        display={IsLargeOrBigger(breakpoint) ? "flex" : "none"}
        w="50%"
        bg="blue.300"
      ></Flex>
    </Flex>
  );
};
